import React, { useState } from 'react';

export default function StudentRemovalModal({ 
  isOpen, 
  onClose, 
  students, 
  periodName, 
  onConfirmRemoval 
}) {
  const [removalType, setRemovalType] = useState('period'); // 'period' or 'class'
  const [isRemoving, setIsRemoving] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsRemoving(true);
    try {
      await onConfirmRemoval(students, removalType);
      onClose();
    } catch (error) {
      console.error('Error removing students:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClose = () => {
    if (!isRemoving) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Remove Students
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {students.length} student{students.length !== 1 ? 's' : ''} selected for removal
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isRemoving}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {/* Student Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Students to be removed:</h3>
            <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {students.map((student, index) => (
                  <div key={student.student_id || student.id || index} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {student.first_name?.[0]}{student.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {student.first_name} {student.last_name}
                        </p>
                        {student.email && (
                          <p className="text-xs text-gray-500">{student.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Removal Options */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Removal Options:</h3>
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="removalType"
                  value="period"
                  checked={removalType === 'period'}
                  onChange={(e) => setRemovalType(e.target.value)}
                  disabled={isRemoving}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Remove from {periodName} only
                  </p>
                  <p className="text-xs text-gray-600">
                    Students will remain enrolled in other periods of this class
                  </p>
                </div>
              </label>
              
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="removalType"
                  value="class"
                  checked={removalType === 'class'}
                  onChange={(e) => setRemovalType(e.target.value)}
                  disabled={isRemoving}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Remove from entire class
                  </p>
                  <p className="text-xs text-gray-600">
                    Students will be unenrolled from all periods in this class
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Warning */}
          {/*<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This action cannot be undone. {removalType === 'period' 
                      ? `Students will be removed from ${periodName} but will remain in other periods.`
                      : 'Students will be completely unenrolled from this class and all its periods.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>*/}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isRemoving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isRemoving}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 flex items-center space-x-2"
          >
            {isRemoving && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>
              {isRemoving ? 'Removing...' : `Remove ${students.length} Student${students.length !== 1 ? 's' : ''}`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
